import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Pagination } from '@/components/ui/pagination';
import { num } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  automationService,
  type Strategy,
  type BacktestBatchItem,
  type BacktestBatchResultItem,
  type SymbolListSummary,
  type ScenarioSummary,
  type ComparisonSummary,
  type ComparisonRow,
} from '@/services/strategyService';
import { BacktestRunDetail } from './BacktestRunDetail';
import { ComparisonScoreboard } from './ComparisonScoreboard';
import { ShareLinkDialog } from '@/components/trade-viewer/ShareLinkDialog';
import type { SavedComparison } from '@/services/strategyService';
import { FlaskConical, Play, RefreshCw, ArrowUpDown, AlertTriangle, X, Copy, History, Save, Bookmark, Trash2, Search, ArrowLeft, Share2, Check } from 'lucide-react';

// Run strategies in chunks so the synchronous batch endpoint (cap 50/call) stays responsive and
// the ranking table fills progressively. Asset-class-agnostic — each strategy carries its own
// AssetClass, so equity + forex (+ future crypto/options) rank in one table.
const CHUNK = 12;

/** A filter that reads as on or off at a glance, unlike a native checkbox. */
const FilterToggle: React.FC<{ label: string; on: boolean; onToggle: () => void }> = ({ label, on, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={on}
    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors
      ${on
        ? 'border-primary/60 bg-primary/10 text-foreground'
        : 'border-transparent bg-muted/60 text-muted-foreground hover:bg-muted'}`}
  >
    <span className={`flex h-3 w-3 items-center justify-center rounded-full border transition-colors
      ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>
      {on && <Check className="h-2 w-2" />}
    </span>
    {label}
  </button>
);

// Default history page size. The user can change it; the request is server-paged either way.
const HISTORY_PAGE_SIZE_DEFAULT = 25;

// Today (YYYY-MM-DD) for the End date <input max>. Computed once at module load — a backtest
// can't end in the future, and a stable string keeps the input referentially clean.
const today = new Date().toISOString().slice(0, 10);

// Timeframes a strategy can be swept across. Each (strategy × TF) is one backtest run; the ranking
// shows them as separate rows so you can read the best TF per strategy straight off the table.
const ALL_TIMEFRAMES = ['1m', '15m', '30m', '1h', '4h', '1D'] as const;

// Ranking-table rows are keyed by strategy AND timeframe — a sweep produces one row per pair.
const rowKey = (r: { strategyId: string; timeframe: string }) => `${r.strategyId}__${r.timeframe}`;

// Sort the TF column by bar DURATION, not lexically (else "1D" sorts between "15m" and "1h").
const TF_MINUTES: Record<string, number> = { '1m': 1, '15m': 15, '30m': 30, '1h': 60, '4h': 240, '1D': 1440 };

type SortKey = 'totalPnL' | 'winRate' | 'profitFactor' | 'avgRMultiple' | 'maxDrawdownPercent' | 'tradeCount' | 'strategyName' | 'assetClass' | 'timeframe' | 'pctResolvedByExit';

// Sort keys for the "Previous runs" history table — sent to the server (camelCase keys map there).
type HistorySortKey = 'name' | 'createdAt' | 'tradeCount' | 'totalPnL' | 'winRate' | 'profitFactor' | 'maxDrawdownPercent' | 'status';


// Open run/comparison detail lives in the hash query (`…?run=<id>` / `…?comparison=<id>`),
// so a detail view is a real bookmarkable/shareable URL rather than transient modal state.
// These helpers read/write the query WITHOUT changing the tab path before the `?`, so the
// lab stays put under #/dashboard/<tab>. `run` is kept alongside `comparison` so drilling
// from a comparison into a run can navigate back to the comparison.
function hashQuery(): URLSearchParams {
  const q = (window.location.hash || '').split('?')[1];
  return new URLSearchParams(q || '');
}
function setHashQuery(params: URLSearchParams) {
  const base = (window.location.hash || '#/').split('?')[0];
  const s = params.toString();
  window.location.hash = s ? `${base}?${s}` : base;
}
function readRunParam(): string | null { return hashQuery().get('run'); }
function readComparisonParam(): string | null { return hashQuery().get('comparison'); }
function openRunHash(runId: string) { const p = hashQuery(); p.set('run', runId); setHashQuery(p); }
function closeRunHash() { const p = hashQuery(); p.delete('run'); setHashQuery(p); } // back to comparison if open
function openComparisonHash(id: string) { const p = new URLSearchParams(); p.set('comparison', id); setHashQuery(p); }
function closeComparisonHash() { setHashQuery(new URLSearchParams()); }

// Flatten a History summary row into a ComparisonRow so any hand-picked set of past runs
// (across different batches) can be assembled into a scoreboard without re-fetching each run.
function summaryToComparisonRow(s: ScenarioSummary): ComparisonRow {
  const st = s.statistics;
  return {
    runId: s.id,
    strategyId: s.strategyId,
    strategyName: s.name,
    assetClass: s.strategySnapshot?.assetClass ?? 'Equity',
    timeframe: (s.strategySnapshot as any)?.timeframe ?? '',
    status: s.status,
    tradeCount: s.tradeCount ?? st?.totalTrades ?? 0,
    totalPnL: st?.totalPnL ?? null,
    winRate: st?.winRate ?? null,
    profitFactor: st?.profitFactor ?? null,
    avgRMultiple: st?.avgRMultiple ?? null,
    maxDrawdownPercent: st?.maxDrawdownPercent ?? null,
    pctResolvedByExit: st?.pctResolvedByExit ?? null,
    avgOpeningRangeWidth: st?.avgOpeningRangeWidth ?? null,
    sharpeRatio: st?.sharpeRatio ?? null,
    recoveryFactor: st?.recoveryFactor ?? null,
    lrCorrelation: st?.lrCorrelation ?? null,
  };
}

export function BacktestLabPage() {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [assetFilter, setAssetFilter] = useState<string>('All');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lookbackDays, setLookbackDays] = useState(365);
  const [endDate, setEndDate] = useState(''); // optional YYYY-MM-DD; blank = up to latest data
  const [timeframes, setTimeframes] = useState<Set<string>>(new Set(['1m'])); // TFs to sweep per strategy
  const [symbolLists, setSymbolLists] = useState<SymbolListSummary[]>([]);
  const [universeId, setUniverseId] = useState<string>(''); // '' = each strategy's own symbols (no common universe)
  const [accountEquity, setAccountEquity] = useState<string>(''); // '' = strategy's own sizing; else $ account override
  const [running, setRunning] = useState(false);
  // Bookmarkable detail deep links — the open run / comparison id lives in the hash
  // (`?run=<id>` / `?comparison=<id>`), not in transient modal state, so each detail view
  // is a shareable URL. null = nothing open (show the lab/history tables).
  const [runParam, setRunParam] = useState<string | null>(() => readRunParam());
  const [comparisonParam, setComparisonParam] = useState<string | null>(() => readComparisonParam());
  const [comparisonDetail, setComparisonDetail] = useState<SavedComparison | null>(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [shareComparisonOpen, setShareComparisonOpen] = useState(false);
  // Ad-hoc comparison builder — hand-pick runs from History (across batches) → assemble a
  // scoreboard. `pickedRuns` keeps the full summary (by runId, survives paging) so we can read
  // each run's CONDITIONS and warn when a hand-picked set isn't apples-to-apples. A single saved
  // batch comparison is fair by construction; ad-hoc isn't, so we flag mismatches.
  const [pickedRuns, setPickedRuns] = useState<Map<string, ScenarioSummary>>(new Map());
  const [adhocComparison, setAdhocComparison] = useState<SavedComparison | null>(null);
  const [adhocWarning, setAdhocWarning] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<Record<string, BacktestBatchResultItem>>({});
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'totalPnL', dir: 'desc' });
  // Previous-runs history — server-paged. `history` holds the CURRENT page; `historyTotal` the full
  // filtered count. Sort/filter run in the DB so they apply across all runs, not just the page.
  const [history, setHistory] = useState<ScenarioSummary[] | null>(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [historyCompletedOnly, setHistoryCompletedOnly] = useState(false);
  const [historyProfitableOnly, setHistoryProfitableOnly] = useState(false);
  const [historyMinTrades, setHistoryMinTrades] = useState('');
  const [historySort, setHistorySort] = useState<{ key: HistorySortKey; dir: 'asc' | 'desc' }>({ key: 'createdAt', dir: 'desc' });
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(HISTORY_PAGE_SIZE_DEFAULT);
  const [tab, setTab] = useState<'lab' | 'history'>('lab');
  const [showMoreMetrics, setShowMoreMetrics] = useState(false); // niche ranking columns (Resolv%, OR width)
  // Saved comparisons (the whole head-to-head set).
  const [savedComparisons, setSavedComparisons] = useState<ComparisonSummary[] | null>(null);
  const [savedLoading, setSavedLoading] = useState(false);
  const [loadedComparisonName, setLoadedComparisonName] = useState<string | null>(null);

  // Keep the deep-link params in sync with the hash (back/forward, external link paste).
  useEffect(() => {
    const onHash = () => { setRunParam(readRunParam()); setComparisonParam(readComparisonParam()); };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Load the saved comparison whenever its deep link is open (and no run drill-down is on top).
  useEffect(() => {
    if (!comparisonParam || runParam) return;
    let cancelled = false;
    setComparisonLoading(true); setComparisonDetail(null);
    automationService.getComparison(comparisonParam)
      .then(c => { if (!cancelled) setComparisonDetail(c); })
      .catch(() => { if (!cancelled) setError('Failed to load this comparison (it may have been deleted).'); })
      .finally(() => { if (!cancelled) setComparisonLoading(false); });
    return () => { cancelled = true; };
  }, [comparisonParam, runParam]);

  useEffect(() => {
    (async () => {
      try {
        const page = await automationService.getRulesPage({ pageSize: 2000 });
        setStrategies(page.items);
        setTotal(page.total ?? page.items.length);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load strategies');
      } finally {
        setLoading(false);
      }
    })();
    // Symbol lists for the common-universe picker (best-effort — a failure just leaves "own symbols").
    automationService.getSymbolLists().then(setSymbolLists).catch(() => {});
    // Saved comparisons up front so the "Saved" card is populated without an extra click.
    automationService.listComparisons().then(setSavedComparisons).catch(() => {});
  }, []);

  // Asset-class options come from the data, so crypto/options appear automatically when added.
  const assetClasses = useMemo(
    () => ['All', ...Array.from(new Set(strategies.map(s => s.assetClass || 'Equity'))).sort()],
    [strategies],
  );

  const filtered = useMemo(
    () => strategies.filter(s => assetFilter === 'All' || (s.assetClass || 'Equity') === assetFilter),
    [strategies, assetFilter],
  );

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllFiltered = () => setSelected(new Set(filtered.map(s => s.id)));
  const clearSel = () => setSelected(new Set());

  const runBatch = useCallback(async () => {
    const ids = filtered.filter(s => selected.has(s.id)).map(s => s.id);
    const tfs = ALL_TIMEFRAMES.filter(tf => timeframes.has(tf));
    if (ids.length === 0 || tfs.length === 0) return;
    // Common universe + account-size override so a head-to-head compares every strategy on the SAME
    // symbols and (optionally) the same account size — the whole point of a fair comparison.
    const acct = accountEquity.trim() ? Number(accountEquity) : NaN;
    const acctOverride = Number.isFinite(acct) && acct > 0 ? acct : null;
    // Cross-product: every selected strategy × every selected timeframe = one run each.
    const allItems: BacktestBatchItem[] = ids.flatMap(id =>
      tfs.map(tf => ({
        strategyId: id, lookbackDays, endDate: endDate || null, backtestTimeframe: tf,
        symbolListId: universeId || null,
        accountEquity: acctOverride, maxNotional: acctOverride,
      })));
    // Drop any open run-detail view — it belongs to the previous batch's results.
    closeRunHash();
    setRunning(true); setError(null); setResults({}); setProgress({ done: 0, total: allItems.length });
    let failedChunks = 0;
    // Per-chunk try so one failing chunk (e.g. a mid-run API blip) doesn't discard the whole batch.
    for (let i = 0; i < allItems.length; i += CHUNK) {
      const chunk = allItems.slice(i, i + CHUNK);
      try {
        const rows = await automationService.runBacktestBatch(chunk);
        // Key by strategy+timeframe so a sweep's rows don't overwrite each other.
        setResults(prev => { const n = { ...prev }; for (const r of rows) n[rowKey(r)] = r; return n; });
      } catch (e: any) {
        failedChunks++;
        setError(`Some batches failed (${failedChunks}). Last error: ${e?.message ?? 'unknown'} (batch backtest is SuperAdmin-only).`);
      }
      setProgress({ done: Math.min(i + CHUNK, allItems.length), total: allItems.length });
    }
    setRunning(false);
  }, [filtered, selected, lookbackDays, endDate, timeframes, universeId, accountEquity]);

  // ── Saved comparisons ──
  const loadSavedComparisons = useCallback(async () => {
    setSavedLoading(true); setError(null);
    try { setSavedComparisons(await automationService.listComparisons()); }
    catch (e: any) { setError(`Failed to load saved comparisons: ${e?.message ?? 'unknown'}`); }
    finally { setSavedLoading(false); }
  }, []);

  const saveComparison = useCallback(async () => {
    const rows = Object.values(results);
    if (rows.length === 0) return;
    const name = window.prompt('Name this comparison:', `Comparison ${new Date().toLocaleString()}`);
    if (!name?.trim()) return;
    const acct = accountEquity.trim() ? Number(accountEquity) : null;
    try {
      await automationService.saveComparison({
        name: name.trim(), notes: null,
        lookbackDays, endDate: endDate || null,
        symbolListId: universeId || null,
        symbolListName: symbolLists.find(l => l.id === universeId)?.name ?? null,
        accountEquity: acct && acct > 0 ? acct : null,
        timeframes: ALL_TIMEFRAMES.filter(tf => timeframes.has(tf)),
        rows: rows.map(r => ({
          runId: r.runId, strategyId: r.strategyId, strategyName: r.strategyName, assetClass: r.assetClass,
          timeframe: r.timeframe, status: r.status, tradeCount: r.tradeCount, totalPnL: r.totalPnL,
          winRate: r.winRate, profitFactor: r.profitFactor, avgRMultiple: r.avgRMultiple,
          maxDrawdownPercent: r.maxDrawdownPercent, pctResolvedByExit: r.pctResolvedByExit,
          avgOpeningRangeWidth: r.avgOpeningRangeWidth,
          sharpeRatio: r.sharpeRatio, recoveryFactor: r.recoveryFactor, lrCorrelation: r.lrCorrelation,
        })),
      });
      setLoadedComparisonName(name.trim());
      loadSavedComparisons();
    } catch (e: any) { setError(`Save failed: ${e?.message ?? 'unknown'}`); }
  }, [results, lookbackDays, endDate, universeId, symbolLists, accountEquity, timeframes, loadSavedComparisons]);

  const loadComparison = useCallback(async (id: string) => {
    setError(null);
    try {
      const c = await automationService.getComparison(id);
      // Restore the ranking table from the saved rows (rows match BacktestBatchResultItem shape).
      const restored: Record<string, BacktestBatchResultItem> = {};
      for (const r of c.rows) restored[rowKey(r as any)] = ({ ...r, symbolsDiscovered: null, barsEmitted: null, zeroTradeReason: null, error: null } as any);
      setResults(restored);
      setLoadedComparisonName(c.name);
      closeRunHash();
    } catch (e: any) { setError(`Failed to load comparison: ${e?.message ?? 'unknown'}`); }
  }, []);

  const deleteComparison = useCallback(async (id: string, name: string) => {
    if (!window.confirm(`Delete saved comparison "${name}"?`)) return;
    try { await automationService.deleteComparison(id); loadSavedComparisons(); }
    catch (e: any) { setError(`Delete failed: ${e?.message ?? 'unknown'}`); }
  }, [loadSavedComparisons]);

  // ── Ad-hoc comparison builder (hand-pick from History) ──
  const togglePick = useCallback((s: ScenarioSummary) => {
    setPickedRuns(prev => {
      const n = new Map(prev);
      if (n.has(s.id)) n.delete(s.id); else n.set(s.id, s);
      return n;
    });
  }, []);

  // Assemble the picked runs into an unsaved scoreboard and open it. Because hand-picked runs
  // can come from DIFFERENT conditions (3-mo vs 1-yr window, different universe), check whether
  // they're comparable and surface a warning — a single saved batch is fair by construction.
  const compareSelected = useCallback(() => {
    if (pickedRuns.size === 0) return;
    const summaries = Array.from(pickedRuns.values());
    const windows = new Set(summaries.map(s => `${s.backtest?.startDate ?? '?'}→${s.backtest?.endDate ?? '?'}`));
    const universes = new Set(summaries.map(s => s.backtest?.symbolListId ?? 'own'));
    const issues: string[] = [];
    if (windows.size > 1) issues.push('date window');
    if (universes.size > 1) issues.push('symbol universe');
    setAdhocWarning(issues.length
      ? `These runs differ in ${issues.join(' & ')} — metrics may not be apples-to-apples. (Account size isn't recorded per run, so verify that too.) A single saved batch comparison guarantees identical conditions.`
      : null);
    const rows = summaries.map(summaryToComparisonRow);
    setAdhocComparison({
      id: '', name: `Ad-hoc comparison (${summaries.length})`, notes: null,
      createdAt: new Date().toISOString(), lookbackDays: 0, endDate: null,
      symbolListId: null, symbolListName: null, accountEquity: null,
      timeframes: Array.from(new Set(rows.map(r => r.timeframe).filter(Boolean))),
      rows,
    });
  }, [pickedRuns]);

  // Persist the unsaved ad-hoc set so it becomes bookmarkable + shareable, then open its page.
  const saveAdhoc = useCallback(async () => {
    if (!adhocComparison) return;
    const name = window.prompt('Name this comparison:', adhocComparison.name);
    if (!name?.trim()) return;
    try {
      const saved = await automationService.saveComparison({
        name: name.trim(), notes: null, lookbackDays: 0, endDate: null,
        symbolListId: null, symbolListName: null, accountEquity: null,
        timeframes: adhocComparison.timeframes, rows: adhocComparison.rows,
      });
      loadSavedComparisons();
      setAdhocComparison(null);
      openComparisonHash(saved.id);   // now bookmarkable + shareable
    } catch (e: any) { setError(`Save failed: ${e?.message ?? 'unknown'}`); }
  }, [adhocComparison, loadSavedComparisons]);

  // Debounce the search box → debouncedQuery (and reset to page 0 when it settles).
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQuery(historyQuery); setHistoryPage(0); }, 300);
    return () => clearTimeout(t);
  }, [historyQuery]);

  // Fetch one server-paged + sorted + filtered page of history. The server does the work so it scales
  // to any number of runs. Reuses the existing run-detail drill-down per row.
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true); setError(null);
    try {
      const { items, total } = await automationService.listScenariosPaged({
        limit: historyPageSize,
        offset: historyPage * historyPageSize,
        sortKey: historySort.key,
        sortDir: historySort.dir,
        search: debouncedQuery,
        completedOnly: historyCompletedOnly,
        profitableOnly: historyProfitableOnly,
        minTrades: historyMinTrades.trim() ? Number(historyMinTrades) : 0,
      });
      setHistory(items);
      setHistoryTotal(total);
    } catch (e: any) {
      setError(`Failed to load history: ${e?.message ?? 'unknown'}`);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyPageSize, historySort, debouncedQuery, historyCompletedOnly, historyProfitableOnly, historyMinTrades]);

  // Fetch a page whenever the History tab is active and any query param changes (fetchHistory's
  // identity changes with page/sort/filters/debouncedQuery, so this re-runs on each).
  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab, fetchHistory]);

  // Clone a strategy so you can tweak params and re-rank the variant against the original — the
  // core "iterate on a strategy" loop. Strips identity/state; backend assigns a fresh id + owner.
  // Force Paper + disabled so a later re-enable can never silently arm a live copy.
  // NOTE: CreateRuleAsync de-dupes by (owner, name) and RETURNS THE EXISTING rule on a name
  // collision (no 409). So we pick the next free "(copy N)" name to actually get a distinct copy,
  // and still dedupe the returned id into local state defensively (re-clone must not double-add).
  const clone = useCallback(async (s: Strategy) => {
    const { id: _id, createdAt: _c, lastUpdatedAt: _u, ...rest } = s;
    const taken = new Set(strategies.map(x => x.name));
    let name = `${s.name} (copy)`;
    for (let n = 2; taken.has(name); n++) name = `${s.name} (copy ${n})`;
    try {
      const created = await automationService.createRule({
        ...rest, name, enabled: false, tradingEnvironment: 'Paper',
      });
      setStrategies(prev => prev.some(x => x.id === created.id) ? prev : [created, ...prev]);
      setTotal(t => strategies.some(x => x.id === created.id) ? t : t + 1);
      setSelected(prev => new Set(prev).add(created.id));
    } catch (e: any) {
      setError(`Clone failed: ${e?.message ?? 'unknown'}`);
    }
  }, [strategies]);

  const ranked = useMemo(() => {
    const rows = Object.values(results);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      if (sort.key === 'timeframe')
        return ((TF_MINUTES[a.timeframe] ?? 0) - (TF_MINUTES[b.timeframe] ?? 0)) * dir;
      const av = (a as any)[sort.key], bv = (b as any)[sort.key];
      if (typeof av === 'string' || typeof bv === 'string')
        return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
      // nulls always last regardless of direction
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir;
    });
  }, [results, sort]);

  // Server-side: `history` IS the current page; total + page count come from the server, so sort and
  // filters apply across ALL runs, not just the visible page.

  // Sort runs server-side; changing it resets to page 0 (the fetch effect re-queries).
  const setHistorySortKey = (key: HistorySortKey) =>
    setHistorySort(prev => { setHistoryPage(0); return { key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }; });
  const hTh = (key: HistorySortKey, label: string, cls = '') => (
    <th className={`px-3 py-2 cursor-pointer select-none hover:text-foreground ${cls}`} onClick={() => setHistorySortKey(key)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className={`h-3 w-3 ${historySort.key === key ? 'opacity-80' : 'opacity-30'}`} /></span>
    </th>
  );

  const setSortKey = (key: SortKey) =>
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));

  const th = (key: SortKey, label: string, cls = '') => (
    <th className={`px-3 py-2 cursor-pointer select-none hover:text-foreground ${cls}`} onClick={() => setSortKey(key)}>
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="h-3 w-3 opacity-40" /></span>
    </th>
  );

  const selectedCount = filtered.filter(s => selected.has(s.id)).length;

  // Bookmarkable run detail — a `?run=<id>` deep link renders the rich detail as its own
  // full view (with Back + Share), not a modal. Back returns to the comparison if one is
  // open underneath (drilled in from a scoreboard), else to the lab.
  if (runParam) {
    return (
      <div className="space-y-4">
        <BacktestRunDetail runId={runParam} onBack={closeRunHash} />
      </div>
    );
  }

  // Bookmarkable comparison scoreboard — a `?comparison=<id>` deep link renders the saved
  // head-to-head as an information-rich dashboard (KPIs + charts + sortable table) with
  // drill-down rows into each run, plus Back + Share (public read-only link).
  if (comparisonParam) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={closeComparisonHash} className="px-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h2 className="text-base font-semibold truncate">
              {comparisonDetail?.name || (comparisonLoading ? 'Loading comparison…' : 'Comparison')}
            </h2>
          </div>
          {comparisonDetail && (
            <Button variant="outline" size="sm" onClick={() => setShareComparisonOpen(true)}>
              <Share2 className="h-4 w-4 mr-1" /> Share
            </Button>
          )}
        </div>
        {comparisonLoading ? (
          <div className="text-sm text-muted-foreground">Loading comparison…</div>
        ) : !comparisonDetail ? (
          <div className="text-sm text-red-500">Couldn't load this comparison (it may have been deleted).</div>
        ) : (
          <ComparisonScoreboard comparison={comparisonDetail} onRowClick={openRunHash} />
        )}
        {comparisonDetail && (
          <ShareLinkDialog
            open={shareComparisonOpen}
            onOpenChange={setShareComparisonOpen}
            title="Share comparison"
            entityName={comparisonDetail.name}
            sharePath="shared-comparison"
            description="Anyone with the link sees the read-only scoreboard until it expires or you revoke it."
            api={{
              list: () => automationService.listComparisonShares(comparisonParam),
              create: (expiresAt) => automationService.createComparisonShare(comparisonParam, expiresAt),
              revoke: (token) => automationService.revokeComparisonShare(comparisonParam, token),
            }}
          />
        )}
      </div>
    );
  }

  // Ad-hoc (unsaved) comparison — hand-picked runs assembled into a scoreboard. Save it to make
  // it bookmarkable + shareable. Row drill-down works (state survives the run-detail round-trip).
  if (adhocComparison) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => setAdhocComparison(null)} className="px-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <h2 className="text-base font-semibold truncate">{adhocComparison.name}</h2>
            <Badge variant="outline" className="text-[10px]">unsaved</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={saveAdhoc}>
            <Save className="h-4 w-4 mr-1" /> Save &amp; share
          </Button>
        </div>
        {adhocWarning && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{adhocWarning}</span>
          </div>
        )}
        <ComparisonScoreboard comparison={adhocComparison} onRowClick={openRunHash} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <FlaskConical className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Backtest Lab</h2>
          <p className="text-sm text-muted-foreground">Batch-backtest many strategies and rank the results — any asset class.</p>
        </div>
      </div>

      {/* Tabs: Run & Compare vs persisted History. */}
      <div className="flex items-center gap-1 border-b">
        {([['lab', 'Run & Compare'], ['history', 'History']] as const).map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === key ? 'border-primary text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {label}{key === 'history' && history ? ` (${history.length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'lab' && (<>
      {/* Controls */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Run a comparison</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Group 1 — universe & window */}
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">1 · Universe &amp; window</div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-sm">
                  <div className="text-muted-foreground mb-1">Asset class</div>
                  <select className="border rounded-md px-2 py-1 bg-background" value={assetFilter} onChange={e => setAssetFilter(e.target.value)}>
                    {assetClasses.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <div className="text-muted-foreground mb-1">Universe <span className="opacity-60" title="Run every strategy on the SAME symbols — required for a fair head-to-head.">ⓘ</span></div>
                  <select className="border rounded-md px-2 py-1 bg-background max-w-[200px]" value={universeId}
                    onChange={e => setUniverseId(e.target.value)} disabled={running}>
                    <option value="">Each strategy's own symbols</option>
                    {symbolLists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.symbols.length})</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <div className="text-muted-foreground mb-1">Lookback (days)</div>
                  <input type="number" min={1} max={3650} className="border rounded-md px-2 py-1 bg-background w-24"
                    value={lookbackDays} onChange={e => { const n = Number(e.target.value); setLookbackDays(Number.isFinite(n) && n >= 1 ? Math.min(3650, n) : 365); }} />
                </label>
                <label className="text-sm">
                  <div className="text-muted-foreground mb-1">End date</div>
                  <input type="date" className="border rounded-md px-2 py-1 bg-background" max={today}
                    value={endDate} onChange={e => setEndDate(e.target.value)} />
                </label>
                <label className="text-sm">
                  <div className="text-muted-foreground mb-1">Account $</div>
                  <input type="number" min={0} step={1000} placeholder="default" className="border rounded-md px-2 py-1 bg-background w-28"
                    value={accountEquity} onChange={e => setAccountEquity(e.target.value)} disabled={running}
                    title="Override account size for all runs (also caps per-trade notional). Blank = each strategy's own sizing." />
                </label>
              </div>
            </div>
            {/* Group 2 — what to run */}
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">2 · What to run</div>
              <div className="text-sm">
                <div className="text-muted-foreground mb-1">Timeframes (sweep)</div>
                <div className="flex gap-1 flex-wrap">
                  {ALL_TIMEFRAMES.map(tf => (
                    <button key={tf} type="button" disabled={running}
                      onClick={() => setTimeframes(prev => { const n = new Set(prev); n.has(tf) ? n.delete(tf) : n.add(tf); return n; })}
                      className={`px-2 py-1 rounded-md border text-xs ${timeframes.has(tf) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted/50'} disabled:opacity-40`}>
                      {tf}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={selectAllFiltered} disabled={running}>Select all ({filtered.length})</Button>
                <Button variant="outline" size="sm" onClick={clearSel} disabled={running || selected.size === 0}>Clear</Button>
                <Button size="sm" onClick={runBatch} disabled={running || selectedCount === 0 || timeframes.size === 0}>
                  {running ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                  Run {selectedCount}×{timeframes.size} = {selectedCount * timeframes.size} run{selectedCount * timeframes.size === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          </div>

          {/* Effective-config summary — what this run will actually do, in one line. */}
          <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 border-t pt-2">
            <span className="px-1.5 py-0.5 rounded bg-muted/60 text-foreground">{selectedCount} selected</span>
            <span>· {universeId ? (symbolLists.find(l => l.id === universeId)?.name ?? 'universe') : "each strategy's own symbols"}</span>
            <span>· {lookbackDays}d{endDate ? ` → ${endDate}` : ''}</span>
            <span>· {accountEquity.trim() ? `$${Number(accountEquity).toLocaleString()} acct` : 'strategy sizing'}</span>
            <span>· {[...timeframes].join(', ') || 'pick a timeframe'}</span>
          </div>

          {/* Progress bar */}
          {progress && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground"><span>Running…</span><span>{progress.done}/{progress.total}</span></div>
              <div className="h-1.5 w-full rounded bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress.total ? Math.round(progress.done / progress.total * 100) : 0}%` }} />
              </div>
            </div>
          )}
          {error && (
            <div className="text-sm text-red-500 flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{error}</div>
          )}
        </CardContent>
      </Card>

      {/* Selection list */}
      <Card>
        <CardHeader><CardTitle className="text-sm">
          Strategies ({filtered.length}{assetFilter !== 'All' ? ` · ${assetFilter}` : ''})
          {total > strategies.length && <span className="ml-2 text-xs font-normal text-amber-500">showing {strategies.length} of {total} — refine by asset class</span>}
        </CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
            /* Selectable tiles rather than a checkbox list: picking a dozen
               strategies is the primary action on this page, so the hit target is
               the whole tile and the selected set is legible at a glance. The
               checkbox role keeps it operable from the keyboard now that the
               native input is gone. */
            <div className="max-h-72 overflow-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-2 p-0.5">
              {filtered.map(s => {
                const on = selected.has(s.id);
                return (
                  <div
                    key={s.id}
                    role="checkbox"
                    aria-checked={on}
                    aria-disabled={running}
                    tabIndex={running ? -1 : 0}
                    title={s.name}
                    onClick={() => { if (!running) toggle(s.id); }}
                    onKeyDown={e => {
                      if (running) return;
                      // Only when the TILE itself has focus. Enter/Space on the nested
                      // Clone button bubbles up here, and swallowing it would
                      // preventDefault the button's own activation and toggle the
                      // selection instead - leaving Clone reachable by keyboard but
                      // impossible to actually operate.
                      if (e.target !== e.currentTarget) return;
                      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(s.id); }
                    }}
                    className={`group flex flex-col gap-1.5 rounded-lg border p-2.5 transition-all outline-none
                      focus-visible:ring-2 focus-visible:ring-primary/50
                      ${running ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      ${on
                        ? 'border-primary/60 bg-primary/[0.07] shadow-sm'
                        : 'hover:border-muted-foreground/40 hover:bg-muted/40'}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className={`mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors
                        ${on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'}`}>
                        {on && <Check className="h-3 w-3" />}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium leading-snug">{s.name}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 pl-6">
                      <Badge variant="outline" className="text-[10px]">{s.assetClass || 'Equity'}</Badge>
                      <button type="button" onClick={e => { e.stopPropagation(); clone(s); }} disabled={running}
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-foreground disabled:opacity-30"
                        title="Clone this strategy (disabled copy) to tweak & re-rank">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Saved comparisons — reopen a whole head-to-head set instantly (restores the ranking table). */}
      {savedComparisons && savedComparisons.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm flex items-center gap-2"><Bookmark className="h-4 w-4" />Saved comparisons ({savedComparisons.length})</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadSavedComparisons} disabled={savedLoading} title="Refresh">
              <RefreshCw className={`h-4 w-4 ${savedLoading ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {savedComparisons.map(c => (
                <div key={c.id} className="group flex items-center gap-2 text-sm px-2 py-1.5 rounded border hover:bg-muted/40">
                  <button type="button" className="min-w-0 flex-1 text-left" onClick={() => openComparisonHash(c.id)}
                    title="Open this comparison scoreboard">
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {c.rowCount} runs{c.symbolListName ? ` · ${c.symbolListName}` : ''}{c.accountEquity ? ` · $${c.accountEquity.toLocaleString()}` : ''} · {new Date(c.createdAt).toLocaleDateString()}
                    </div>
                  </button>
                  <button type="button" onClick={() => loadComparison(c.id)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground" title="Load back into the lab table (to tweak & re-run)">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => deleteComparison(c.id, c.name)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500" title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state — orient before any results exist. */}
      {ranked.length === 0 && !running && (
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <FlaskConical className="h-7 w-7 mx-auto text-muted-foreground/40" />
            <div className="font-medium">Run your first comparison</div>
            <div className="text-sm text-muted-foreground">
              Pick strategies above, choose a <span className="text-foreground">universe</span> + <span className="text-foreground">timeframes</span>, then hit <span className="text-foreground">Run</span>.
            </div>
            <div className="text-xs text-muted-foreground">Results rank here — click any row to drill into its equity curve, trades &amp; chart. Save a set to revisit, or browse past runs in the <span className="text-foreground">History</span> tab.</div>
          </CardContent>
        </Card>
      )}

      {/* Ranking */}
      {ranked.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm flex items-center gap-2">
              Ranking ({ranked.length})
              {loadedComparisonName && <Badge variant="outline" className="text-[10px] font-normal"><Bookmark className="h-3 w-3 mr-1" />{loadedComparisonName}</Badge>}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowMoreMetrics(v => !v)} title="Show/hide Resolv% + OR-width columns">
                {showMoreMetrics ? 'Fewer metrics' : 'More metrics'}
              </Button>
              <Button variant="outline" size="sm" onClick={saveComparison} title="Save this whole comparison set to revisit later">
                <Save className="h-4 w-4 mr-1" />Save comparison
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b sticky top-0 bg-card z-10">
                <tr>
                  {th('strategyName', 'Strategy')}
                  {th('assetClass', 'Asset')}
                  {th('timeframe', 'TF')}
                  {th('tradeCount', 'Trades', 'text-right')}
                  {th('totalPnL', 'PnL', 'text-right')}
                  {th('winRate', 'Win%', 'text-right')}
                  {th('profitFactor', 'PF', 'text-right')}
                  {th('avgRMultiple', 'Avg R', 'text-right')}
                  {th('maxDrawdownPercent', 'MaxDD%', 'text-right')}
                  {showMoreMetrics && th('pctResolvedByExit', 'Resolv%', 'text-right')}
                  {showMoreMetrics && <th className="px-3 py-2 text-right">OR width</th>}
                  <th className="px-3 py-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map(r => (
                  <tr key={rowKey(r)}
                    className="border-b border-border/40 hover:bg-muted/30 cursor-pointer"
                    role="button" tabIndex={0}
                    onClick={() => openRunHash(r.runId)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRunHash(r.runId); } }}
                    title="Click to see this run's equity curve, stats & trades">
                    <td className="px-3 py-1.5 max-w-[220px] truncate" title={r.strategyName}>{r.strategyName}</td>
                    <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px]">{r.assetClass}</Badge></td>
                    <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px] font-mono">{r.timeframe}</Badge></td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.tradeCount}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${(r.totalPnL ?? 0) > 0 ? 'text-emerald-500' : (r.totalPnL ?? 0) < 0 ? 'text-red-500' : ''}`}>{num(r.totalPnL, 0)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.winRate == null ? '—' : num(r.winRate, 1)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.profitFactor == null ? '—' : num(r.profitFactor, 2)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.avgRMultiple == null ? '—' : num(r.avgRMultiple, 2)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.maxDrawdownPercent == null ? '—' : num(r.maxDrawdownPercent, 1)}</td>
                    {showMoreMetrics && <td className="px-3 py-1.5 text-right tabular-nums" title="% of trades that hit a real price level (vs drifted to a forced close)">{r.pctResolvedByExit == null ? '—' : num(r.pctResolvedByExit, 0)}</td>}
                    {showMoreMetrics && <td className="px-3 py-1.5 text-right tabular-nums" title="Avg opening-range width (RangeMultiple/ORB strategies)">{r.avgOpeningRangeWidth ? num(r.avgOpeningRangeWidth, 2) : '—'}</td>}
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">
                      {r.status !== 'Completed' ? <span className="text-red-500">{r.status}{r.error ? `: ${r.error}` : ''}</span>
                        : r.zeroTradeReason ? <span title={`symbols=${r.symbolsDiscovered ?? '?'} bars=${r.barsEmitted ?? '?'}`}>{r.zeroTradeReason}</span>
                        : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </CardContent>
        </Card>
      )}
      </>)}

      {/* Previous runs — persisted backtest history. Every past run is retrievable; click a row to
          drill into its summary + individual trades via the same detail panel below. */}
      {tab === 'history' && (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2 whitespace-nowrap"><History className="h-4 w-4" />
            Previous runs{history !== null ? ` (${historyTotal})` : ''}
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input type="text" placeholder="Search name…" value={historyQuery}
                onChange={e => setHistoryQuery(e.target.value)}
                className="border rounded-md pl-7 pr-2 py-1 bg-background text-sm w-44" />
            </div>
            {/* Toggle chips: same two filters, but the state is readable across the
                row instead of hiding in a 13px native checkbox. */}
            <FilterToggle
              label="Completed only"
              on={historyCompletedOnly}
              onToggle={() => { setHistoryPage(0); setHistoryCompletedOnly(v => !v); }}
            />
            <FilterToggle
              label="Profitable only"
              on={historyProfitableOnly}
              onToggle={() => { setHistoryPage(0); setHistoryProfitableOnly(v => !v); }}
            />
            <label className="text-xs flex items-center gap-1 text-muted-foreground">
              Min trades
              <input type="number" min={0} value={historyMinTrades} onChange={e => { setHistoryPage(0); setHistoryMinTrades(e.target.value); }}
                className="border rounded-md px-1.5 py-1 bg-background text-sm w-16" placeholder="0" />
            </label>
            {pickedRuns.size > 0 && (
              <>
                <Button size="sm" onClick={compareSelected}>
                  <Bookmark className="h-4 w-4 mr-1" /> Compare selected ({pickedRuns.size})
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPickedRuns(new Map())} title="Clear selection">
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={fetchHistory} disabled={historyLoading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${historyLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        {history && (
          <CardContent className="overflow-auto">
            {history.length === 0 ? <div className="text-sm text-muted-foreground">{historyLoading ? 'Loading…' : 'No runs match.'}</div> : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground border-b">
                  <tr>
                    <th className="px-2 py-2 w-8" title="Pick runs to compare"></th>
                    {hTh('name', 'Run')}
                    {hTh('createdAt', 'When')}
                    {hTh('tradeCount', 'Trades', 'text-right')}
                    {hTh('totalPnL', 'PnL', 'text-right')}
                    {hTh('winRate', 'Win%', 'text-right')}
                    {hTh('profitFactor', 'PF', 'text-right')}
                    {hTh('maxDrawdownPercent', 'MaxDD%', 'text-right')}
                    {hTh('status', 'Status')}
                  </tr>
                </thead>
                <tbody>
                  {history.map(s => (
                    <tr key={s.id}
                      className={`border-b border-border/40 hover:bg-muted/30 cursor-pointer ${pickedRuns.has(s.id) ? 'bg-primary/5' : ''}`}
                      role="button" tabIndex={0}
                      onClick={() => openRunHash(s.id)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRunHash(s.id); } }}
                      title="Click to see this run's equity curve, stats & trades">
                      <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={pickedRuns.has(s.id)} onChange={() => togglePick(s)}
                          title="Pick this run to compare" className="cursor-pointer" />
                      </td>
                      <td className="px-3 py-1.5 max-w-[320px] truncate" title={s.name}>{s.name}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">{new Date(s.createdAt).toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{s.tradeCount}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${(s.statistics?.totalPnL ?? 0) > 0 ? 'text-emerald-500' : (s.statistics?.totalPnL ?? 0) < 0 ? 'text-red-500' : ''}`}>{s.statistics ? num(s.statistics.totalPnL, 0) : '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{s.statistics ? num(s.statistics.winRate, 1) : '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{s.statistics ? num(s.statistics.profitFactor, 2) : '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{s.statistics ? num(s.statistics.maxDrawdownPercent, 1) : '—'}</td>
                      <td className="px-3 py-1.5 text-xs">{s.status !== 'Completed' ? <span className="text-red-500">{s.status}</span> : <span className="text-muted-foreground">Completed</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {/* Paging — server-side; total comes from the API. The pager is 1-based,
                this page's state is 0-based, so the two are bridged here. */}
            {historyTotal > 0 && (
              <div className="mt-3 border-t pt-3">
                <Pagination
                  page={historyPage + 1}
                  pageSize={historyPageSize}
                  total={historyTotal}
                  onPageChange={(p) => setHistoryPage(p - 1)}
                  onPageSizeChange={(size) => { setHistoryPageSize(size); setHistoryPage(0); }}
                  loading={history === null}
                  itemLabel="runs"
                />
              </div>
            )}
          </CardContent>
        )}
      </Card>
      )}
    </div>
  );
}
